import { type PaginationState } from '@tanstack/react-table'
import { type Dispatch, type FC, type SetStateAction } from 'react'

import EnterpriseFiltersView, { type FilterConfigurations } from 'components/reusable/filters/EnterpriseFiltersView'
import { type ComboboxFilterGroup } from 'components/reusable/table/ComboboxMultiSelectFilter'

import EmployerBankOnboardingStatusFilter from './EmployerBankOnboardingStatusFilter'
import EmployerBankPartnersFilter from './EmployerBankPartnersFilter'
import { type EmployerFilters } from './EmployerReviewsTable'
import EmployerReviewStatusFilter from './EmployerReviewStatusFilter'
import OnboardingStatusFilter from './OnboardingStatusFilter'
import PlaidAccountVerificationStatusFilter from './PlaidAccountVerificationStatusFilter'

export type EmployerFilterGroupType = 'Employer'

type SingleSelectFilterComponents = Record<
  string,
  FC<any>
>

type MultiSelectFilterComponents = Record<
  string,
  FC<any>
>

export const employerSingleSelectFilters: SingleSelectFilterComponents = {
  bank_onboarding_status: EmployerBankOnboardingStatusFilter,
}

export const employerMultiSelectFilters: MultiSelectFilterComponents = {
  review_statuses: EmployerReviewStatusFilter,
  bank_partners: EmployerBankPartnersFilter,
  plaid_account_verification_status: PlaidAccountVerificationStatusFilter,
  onboarding_status: OnboardingStatusFilter,
}

export const filterConfigurations: FilterConfigurations<
  EmployerFilters,
  EmployerFilterGroupType
> = {
  review_statuses: {
    key: 'review_statuses',
    label: 'Review Status',
    group: 'Employer',
    isMultiValue: true,
  },
  bank_partners: {
    key: 'bank_partners',
    label: 'Bank Partners',
    group: 'Employer',
    isMultiValue: true,
  },
  bank_onboarding_status: {
    key: 'bank_onboarding_status',
    label: 'Bank Requirements Status',
    group: 'Employer',
    isMultiValue: false,
  },
  plaid_account_verification_status: {
    key: 'plaid_account_verification_status',
    label: 'Plaid Connection',
    group: 'Employer',
    isMultiValue: true,
  },
  onboarding_status: {
    key: 'onboarding_status',
    label: 'Onboarding Status',
    group: 'Employer',
    isMultiValue: true,
  },
}

export const employerDefaultPinnedOptionsByFilterValue = {
  review_statuses: [],
  bank_partners: [],
  plaid_account_verification_status: [],
  onboarding_status: [],
}

export const getEmployerFiltersConfigurationSortValue = (
  filterConfiguration: ComboboxFilterGroup<string>
): number => {
  switch (filterConfiguration.groupLabel) {
    case 'Employer':
      return 1
    default:
      return 4
  }
}

type EmployerReviewFiltersProps = {
  filters: EmployerFilters
  setFilters: Dispatch<SetStateAction<EmployerFilters>>
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
}

const EmployerReviewFilters = ({
  filters,
  setFilters,
  pagination,
  setPagination,
}: EmployerReviewFiltersProps) => {
  return (
    <EnterpriseFiltersView
      filters={filters}
      setFilters={setFilters}
      pagination={pagination}
      setPagination={setPagination}
      filterConfigurations={filterConfigurations}
      singleSelectFilters={employerSingleSelectFilters}
      multiSelectFilters={employerMultiSelectFilters}
      defaultPinnedOptionsByFilterValue={
        employerDefaultPinnedOptionsByFilterValue
      }
      getFiltersConfigurationSortValue={
        getEmployerFiltersConfigurationSortValue
      }
    />
  )
}

export default EmployerReviewFilters
